import { useCallback, useEffect, useState } from 'react'
import { apiClient } from '../utils/apiClient'
import { getEffectiveConnectionMode } from '../utils/databaseConnectionMode'
import { useOrganizationSettings } from './useOrganizationSettings'
import type { DatabaseNavFlags } from '../utils/databaseContextNav'
import { DATABASES_LIST_CHANGED_EVENT } from '../utils/databasesListEvents'
import { ACTIVE_ORG_CHANGED_EVENT } from '../utils/activeOrganization'

interface SidebarDatabase {
  id: number
  name: string
  forksEnabled?: boolean
  approvedWritesEnabled?: boolean
  hosted?: boolean
  connectionMode?: 'cloud' | 'self_hosted' | 'synthetic'
  attachedProjectIds?: number[]
}

export function useDatabaseSidebarMeta(databaseId: string) {
  const { dataClassificationEnabled, loading: orgSettingsLoading } = useOrganizationSettings()

  const [database, setDatabase] = useState<SidebarDatabase | null>(null)
  const [loading, setLoading] = useState(true)
  const [pendingWritesCount, setPendingWritesCount] = useState<number | null>(null)

  const loadDatabase = useCallback(async () => {
    if (!databaseId) {
      setDatabase(null)
      setLoading(false)
      return
    }
    try {
      const data = await apiClient.get<SidebarDatabase>(`/api/databases/${databaseId}`)
      setDatabase(data)
    } catch {
      setDatabase(null)
    } finally {
      setLoading(false)
    }
  }, [databaseId])

  useEffect(() => {
    if (!databaseId) {
      setDatabase(null)
      setLoading(false)
      return
    }
    setLoading(true)
    void loadDatabase()
    const onChanged = () => void loadDatabase()
    window.addEventListener(DATABASES_LIST_CHANGED_EVENT, onChanged)
    window.addEventListener(ACTIVE_ORG_CHANGED_EVENT, onChanged)
    return () => {
      window.removeEventListener(DATABASES_LIST_CHANGED_EVENT, onChanged)
      window.removeEventListener(ACTIVE_ORG_CHANGED_EVENT, onChanged)
    }
  }, [databaseId, loadDatabase])

  const flags: DatabaseNavFlags = {
    dataClassificationEnabled,
    orgSettingsLoading,
    approvedWritesEnabled: Boolean(database?.approvedWritesEnabled),
    selfHosted: database ? getEffectiveConnectionMode(database) === 'self_hosted' : false,
  }

  const loadPendingWritesCount = useCallback(async () => {
    if (!databaseId || !database?.hosted || !database.approvedWritesEnabled) {
      setPendingWritesCount(null)
      return
    }
    try {
      const data = await apiClient.get<{ items?: unknown[] }>(
        `/api/databases/${databaseId}/gateway-write-requests?status=pending`,
      )
      setPendingWritesCount(Array.isArray(data.items) ? data.items.length : 0)
    } catch {
      setPendingWritesCount(null)
    }
  }, [databaseId, database?.hosted, database?.approvedWritesEnabled])

  useEffect(() => {
    if (orgSettingsLoading || !database?.hosted || !database.approvedWritesEnabled) {
      setPendingWritesCount(null)
      return
    }
    void loadPendingWritesCount()
    const t = window.setInterval(() => void loadPendingWritesCount(), 15000)
    return () => window.clearInterval(t)
  }, [orgSettingsLoading, database?.hosted, database?.approvedWritesEnabled, loadPendingWritesCount])

  return {
    database,
    loading,
    flags,
    pendingWritesCount,
  }
}
